/**
 * Phone numbers leave the portal as the bare 10-digit North American number
 * (`5148257340`): no dashes, spaces or parentheses, and no `+1` country code.
 * The opt-in reply is matched on exactly that value, so a number saved as
 * `514-825-7340` or `+1 514 825 7340` made the customer's YES fail (FD-7805).
 */
export const PHONE_DIGITS = 10;

/**
 * Keeps the digits only and drops a leading `1` country code when the number
 * has eleven digits. Anything that is not a valid number is returned as its
 * digits, so validation can reject it.
 */
export const normalizePhone = (value: string | null | undefined): string => {
  const digits = (value ?? "").replace(/\D/g, "");

  return digits.length === PHONE_DIGITS + 1 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;
};

export const isValidPhone = (value: string | null | undefined): boolean =>
  new RegExp(`^\\d{${PHONE_DIGITS}}$`).test(value ?? "");

/**
 * How a phone number is shown: `514-825-7340`. A value that is not a valid
 * number (an old free-text entry) is shown as it was stored.
 */
export const formatPhone = (value: string | null | undefined): string => {
  const digits = normalizePhone(value);

  return isValidPhone(digits) ? maskPhone(digits) : (value ?? "");
};

/**
 * The `xxx-xxx-xxxx` mask as the number is being typed: `514`, `514-825`,
 * `514-825-7`. More digits than a number holds are left unmasked so the
 * mistake stays visible.
 */
export const maskPhone = (value: string | null | undefined): string => {
  const digits = normalizePhone(value);

  if (digits.length > PHONE_DIGITS) {
    return digits;
  }

  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)]
    .filter(Boolean)
    .join("-");
};
